import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, CardBody, Collapse, Input, Spinner, Table, UncontrolledTooltip } from 'reactstrap';
import Swal from 'sweetalert2';
import { ChevronDown } from 'react-feather';
import { RiStopCircleLine } from 'react-icons/ri';
import { MdOutlineLiveTv } from 'react-icons/md';
import {
  ActiveBotTask,
  BotLogHistoryItem,
  fetchActiveBotTasks,
  fetchIpaLogDetails,
  fetchIpaLogHistory,
  fetchUserPermissions,
  stopActiveBotRunner,
  stopActiveBotTask,
} from './store';
import { getAutomationLogWsUrl, clearBotTokens } from '../../../Service/botRuntimeService';
import WsServicePath from '../../../Service/websocket_webservice';

const getTaskMessagesCache = () => {
  if (!(window as any).taskMessagesCache) {
    (window as any).taskMessagesCache = {
      get: (key: string) => {
        try {
          const val = localStorage.getItem(`task_msg_${key}`);
          return val ? JSON.parse(val) : undefined;
        } catch {
          return undefined;
        }
      },
      set: (key: string, value: any[]) => {
        try {
          localStorage.setItem(`task_msg_${key}`, JSON.stringify(value));
        } catch {}
        return (window as any).taskMessagesCache;
      },
      delete: (key: string) => {
        try {
          const exists = localStorage.getItem(`task_msg_${key}`) !== null;
          localStorage.removeItem(`task_msg_${key}`);
          window.dispatchEvent(new CustomEvent('task_cache_cleared', { detail: { taskId: key } }));
          return exists;
        } catch {
          return false;
        }
      }
    };
  }
  return (window as any).taskMessagesCache;
};

const formatDate = (val?: any) => {
  if (!val) return '—';
  try {
    if (typeof val === 'number') {
      const d = new Date(val > 1e11 ? val : val * 1000);
      return isNaN(d.getTime()) ? String(val) : d.toLocaleString();
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? String(val) : d.toLocaleString();
  } catch {
    return String(val);
  }
};

const renderTaskStatusBadge = (status?: string) => {
  const normalized = (status || '').toUpperCase().trim();
  switch (normalized) {
    case 'C':
    case 'COMPLETED':
      return <Badge color='success'>Completed</Badge>;
    case 'S':
    case 'STOP':
    case 'STOPPED':
      return <Badge color='danger'>Stopped</Badge>;
    case 'E':
    case 'ERROR':
      return <Badge color='danger'>Error</Badge>;
    case 'F':
    case 'FAILED':
      return <Badge color='danger'>Failed</Badge>;
    case 'I':
    case 'RUNNING':
    case 'IN PROGRESS':
      return <Badge color='info'>Running</Badge>;
    case 'Y':
    case 'YET TO START':
      return <Badge color='secondary'>Yet to Start</Badge>;
    default:
      return <Badge color='secondary'>{status || 'Completed'}</Badge>;
  }
};

type BotAssistantProps = {
  focusTaskId?: string | null;
  isActive?: boolean;
};

const BotAssistant = ({ focusTaskId = null, isActive = true }: BotAssistantProps) => {
  const [activeTasks, setActiveTasks] = useState<ActiveBotTask[]>([]);
  const [historyTasks, setHistoryTasks] = useState<BotLogHistoryItem[]>([]);
  const [completedTaskIds, setCompletedTaskIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalHistory, setTotalHistory] = useState(0);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [wsMessages, setWsMessages] = useState<any[]>([]);
  const [historyDetails, setHistoryDetails] = useState<Record<string, { raw_logs: any[]; final_logs: any[]; loading: boolean }>>({});
  const [menuActions, setMenuActions] = useState<string>('');
  const wsRef = useRef<WebSocket | null>(null);
  const activeTasksRef = useRef<ActiveBotTask[]>([]);
  const wsTaskIdRef = useRef<string | null>(null);
  const logContainerRef = useRef<HTMLDivElement | null>(null);
  const broadcastRef = useRef<BroadcastChannel | null>(null);

  // Setup BroadcastChannel for cross-tab log synchronization
  useEffect(() => {
    if (typeof window === "undefined" || !("BroadcastChannel" in window)) {
      return;
    }

    const channel = new BroadcastChannel("bot_ipa_logs_sync");
    broadcastRef.current = channel;

    channel.onmessage = (event) => {
      const data = event.data;
      if (!data || typeof data !== "object") return;

      if (data.type === "NEW_LOG" && data.taskId) {
        if (wsTaskIdRef.current === data.taskId) {
          setWsMessages((previous) => {
            const isNewSession = data.isNewSession;
            const next = isNewSession ? [data.payload] : [...previous, data.payload];
            getTaskMessagesCache().set(data.taskId, next);
            return next;
          });
        }
      } else if (data.type === "CLEAR_TASK_LOGS" && data.taskId) {
        if (wsTaskIdRef.current === data.taskId) {
          setWsMessages([]);
        }
        getTaskMessagesCache().delete(data.taskId);
      }
    };

    return () => {
      channel.close();
      broadcastRef.current = null;
    };
  }, []);

  useEffect(() => {
    activeTasksRef.current = activeTasks;
  }, [activeTasks]);

  const userId = useMemo(() => localStorage.getItem('id') || '', []);
  const username = useMemo(() => localStorage.getItem('UserName') || '', []);
  const totalPages = Math.max(1, Math.ceil(totalHistory / perPage));

  const loadData = async () => {
    setLoading(true);
    try {
      const [activeRes, historyRes] = await Promise.all([
        fetchActiveBotTasks({ q: search, page: 1, perPage: 50 }),
        fetchIpaLogHistory({ q: search, page, perPage }),
      ]);
      setActiveTasks(activeRes.data);
      setHistoryTasks(historyRes.data);
      setTotalHistory(historyRes.count);
    } catch (error: any) {
      Swal.fire('Error', error.message || 'Failed to load IPA bot logs', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [page, perPage]);

  useEffect(() => {
    if (isActive) {
      loadData();
    }
  }, [isActive]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      loadData();
    }, 300);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (userId) {
      fetchUserPermissions(userId).then((response) => {
        if (Array.isArray(response) && response[0]?.Permission) {
          const action = response[0].Permission.find(
            (item: any) => item.MenuKey === 'BOTMANAGER'
          );
          if (action) {
            setMenuActions(action.MenuActions);
          }
        }
      }).catch(err => console.error('Failed to fetch permissions', err));
    }
  }, [userId]);

  // Live activity change stream listener
  useEffect(() => {
    const wsUrl = `${WsServicePath}ws/userActivity/`;
    const ws = new WebSocket(wsUrl);

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data) {
          const updatedDoc = data.fullDocument;
          const opType = data.operationType;
          const docId = updatedDoc?._id?.$oid || data.documentKey?._id?.$oid || (typeof data.documentKey?._id === 'string' ? data.documentKey?._id : null);

          if (docId) {
            if (opType === 'delete' || (updatedDoc && updatedDoc.status === 'R') || (updatedDoc && updatedDoc.bot_status !== 'I')) {
              setActiveTasks((prevTasks) => prevTasks.filter((t) => t.id !== docId));
              // Refresh history logs when active bot completes execution
              loadData();
            } else if (updatedDoc && (opType === 'update' || opType === 'replace')) {
              if (updatedDoc.bot_status === 'I') {
                setCompletedTaskIds((prev) => {
                  if (prev.has(docId)) {
                    const next = new Set(prev);
                    next.delete(docId);
                    return next;
                  }
                  return prev;
                });
              }
              setActiveTasks((prevTasks) =>
                prevTasks.map((t) => {
                  if (t.id === docId) {
                    return {
                      ...t,
                      project_title: updatedDoc.project_title ?? t.project_title,
                      Description: updatedDoc.Description ?? t.Description,
                      bot_status: updatedDoc.bot_status ?? t.bot_status,
                      Domain: updatedDoc.initial_url ?? t.Domain,
                      steps: updatedDoc.steps ?? t.steps,
                    };
                  }
                  return t;
                })
              );
            } else {
              loadData();
            }
          } else {
            loadData();
          }
        }
      } catch (error) {
        console.error('[BotAssistant] WebSocket change stream error:', error);
      }
    };

    return () => {
      ws.close();
    };
  }, []);

  useEffect(() => {
    if (!focusTaskId || loading || activeTasks.length === 0) return;
    const task = activeTasks.find((item) => item.id === focusTaskId);
    if (task && expandedRowId !== focusTaskId) {
      handleActiveRowClick(task);
    }
  }, [focusTaskId, loading, activeTasks]);

  const handleStopBot = async (task: ActiveBotTask) => {
    try {
      await stopActiveBotTask({ userId, username, taskId: task.id, title: task.project_title });
      try {
        await stopActiveBotRunner({
          user_id: userId,
          select_id: task.id,
          title: task.project_title,
          username,
          userid: userId,
        });
      } catch {}
      await loadData();
      Swal.fire('Success', 'Bot stopped successfully', 'success');
    } catch (error: any) {
      Swal.fire('Error', error.message || 'Failed to stop bot', 'error');
    }
  };

  const connectWebSocket = async (task: ActiveBotTask) => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    wsTaskIdRef.current = task.id;
    // Start fresh so backend history replay populates without duplicating
    setWsMessages([]);

    try {
      const wsUrl = await getAutomationLogWsUrl(task.id, userId);
      const ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const msgText = data.message || '';
          const statusText = data.status || '';

          if (typeof msgText === 'string' && msgText.toLowerCase().includes('user id mismatch')) {
            console.warn('[BotAssistant] User ID mismatch detected, clearing bot tokens and reconnecting...');
            clearBotTokens();
            setTimeout(() => {
              connectWebSocket(task);
            }, 500);
            return;
          }

          if (
            (typeof msgText === 'string' && msgText.toLowerCase().includes('automation completed successfully')) ||
            (typeof statusText === 'string' && statusText.toLowerCase().includes('automation completed successfully'))
          ) {
            setCompletedTaskIds((prev) => {
              const next = new Set(prev);
              next.add(task.id);
              return next;
            });
            // Refresh history on completion
            loadData();
          }

          const isNewSession = data.message?.toLowerCase?.().includes('automation started') ||
                               data.message?.toLowerCase?.().includes('starting rpa automation') ||
                               data.message?.toLowerCase?.().includes('starting automation');
          if (isNewSession) {
            setCompletedTaskIds((prev) => {
              if (prev.has(task.id)) {
                const next = new Set(prev);
                next.delete(task.id);
                return next;
              }
              return prev;
            });
          }
          setWsMessages((previous) => {
            const next = isNewSession ? [data] : [...previous, data];
            getTaskMessagesCache().set(task.id, next);
            return next;
          });

          // Broadcast to other open tabs
          if (broadcastRef.current) {
            broadcastRef.current.postMessage({
              type: "NEW_LOG",
              taskId: task.id,
              payload: data,
              isNewSession,
            });
          }
        } catch {
          const fallbackData = { raw: event.data };
          setWsMessages((previous) => {
            const next = [...previous, fallbackData];
            getTaskMessagesCache().set(task.id, next);
            return next;
          });
          if (broadcastRef.current) {
            broadcastRef.current.postMessage({
              type: "NEW_LOG",
              taskId: task.id,
              payload: fallbackData,
              isNewSession: false,
            });
          }
        }
      };
      ws.onerror = (error) => {
        console.error('[BotAssistant] Live WebSocket error:', error);
      };
      ws.onclose = () => {
        wsRef.current = null;
      };
      wsRef.current = ws;
    } catch (error) {
      console.error('[BotAssistant] Failed to connect WebSocket:', error);
    }
  };

  const handleActiveRowClick = async (task: ActiveBotTask) => {
    if (expandedRowId === task.id) {
      setExpandedRowId(null);
      wsTaskIdRef.current = null;
      setWsMessages([]);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      return;
    }

    setExpandedRowId(task.id);
  };

  const handleHistoryRowClick = async (item: BotLogHistoryItem) => {
    const rowKey = `history-${item.task_run_id}`;
    if (expandedRowId === rowKey) {
      setExpandedRowId(null);
      return;
    }

    setExpandedRowId(rowKey);

    // Fetch details lazily if not loaded yet
    if (!historyDetails[item.task_run_id]) {
      setHistoryDetails((prev) => ({
        ...prev,
        [item.task_run_id]: { raw_logs: [], final_logs: [], loading: true },
      }));

      try {
        const details = await fetchIpaLogDetails(item.task_run_id);
        setHistoryDetails((prev) => ({
          ...prev,
          [item.task_run_id]: {
            raw_logs: details.automation_raw_logs || [],
            final_logs: details.final_logs || [],
            loading: false,
          },
        }));
      } catch (err) {
        setHistoryDetails((prev) => ({
          ...prev,
          [item.task_run_id]: { raw_logs: [], final_logs: [], loading: false },
        }));
      }
    }
  };

  useEffect(() => {
    if (isActive && expandedRowId && !expandedRowId.startsWith('history-')) {
      const task = activeTasksRef.current.find((t) => t.id === expandedRowId);
      if (task) {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || wsTaskIdRef.current !== expandedRowId) {
          connectWebSocket(task);
        }
      }
    }
  }, [isActive, expandedRowId]);

  useEffect(() => {
    const scrollToBottom = () => {
      if (logContainerRef.current) {
        logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
      }
    };
    scrollToBottom();
    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, [wsMessages, expandedRowId]);

  return (
    <Card className='border-0 shadow-sm'>
      <CardBody>
        <div className='d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4'>
          <h4 className='mb-0 d-flex align-items-center gap-2'><MdOutlineLiveTv /> IPA Bot Log</h4>
          <div style={{ minWidth: 260 }}>
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder='Search by Activity...' />
          </div>
        </div>

        <div className='table-responsive'>
          <Table hover bordered className='align-middle mb-0'>
            <thead className='table-light'>
              <tr>
                <th>Bot Name</th>
                <th>Username</th>
                <th>Description</th>
                <th>Task Type</th>
                <th>Started Time</th>
                <th>Completed Time</th>
                <th>Duration</th>
                <th>Status</th>
                <th className='text-end'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className='text-center py-4'><Spinner size='sm' /> Loading logs...</td></tr>
              ) : (activeTasks.length === 0 && historyTasks.length === 0) ? (
                <tr><td colSpan={9} className='text-center py-4 text-muted'>No Activity Logs Available</td></tr>
              ) : (
                <>
                  {/* Live Active Bot Executions */}
                  {activeTasks.map((task) => {
                    const isExpanded = expandedRowId === task.id;
                    return (
                      <Fragment key={`active-${task.id}`}>
                        <tr onClick={() => handleActiveRowClick(task)} style={{ cursor: 'pointer', backgroundColor: '#f0fdf4' }}>
                          <td>
                            <div className='d-flex align-items-center gap-2 fw-semibold text-success'>
                              <ChevronDown size={16} style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
                              {task.project_title}
                              <Badge color='success' className='ms-1 px-2 py-1'>LIVE</Badge>
                            </div>
                          </td>
                          <td>{task.Username || username || '—'}</td>
                          <td>{task.Description || '—'}</td>
                          <td>Automation</td>
                          <td>{task.created_date || '—'}</td>
                          <td>—</td>
                          <td>In Progress</td>
                          <td><Badge color='info'>Running</Badge></td>
                          <td className='text-end' onClick={(e) => e.stopPropagation()}>
                            {task.bot_status === 'I' && (menuActions.includes('edit') || menuActions.includes('delete')) ? (
                              <Button
                                id={`stop-bot-${task.id}`}
                                color='link'
                                title={completedTaskIds.has(task.id) ? 'Automation Completed' : 'Stop Bot'}
                                className={`p-0 ${completedTaskIds.has(task.id) ? 'text-muted' : 'text-danger'}`}
                                disabled={completedTaskIds.has(task.id)}
                                onClick={() => handleStopBot(task)}
                              >
                                <RiStopCircleLine size={18} />
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={9} className='p-0 border-0'>
                            <Collapse isOpen={isExpanded}>
                              <div className='m-3 border rounded bg-light p-3'>
                                <div className='d-flex justify-content-between align-items-center mb-3'>
                                  <h6 className='mb-0 text-success fw-bold'>Live Activity Logs</h6>
                                  <span className='text-muted small'>{wsMessages.length} messages</span>
                                </div>
                                <div ref={logContainerRef} style={{ maxHeight: 320, overflowY: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
                                  {wsMessages.length === 0 ? (
                                    <p className='text-muted mb-0'>Waiting for live logs...</p>
                                  ) : wsMessages.map((message, index) => (
                                    <div key={`live-msg-${task.id}-${index}`} className='border-bottom pb-1 mb-1'>
                                      {message.message ? <div className={String(message.message).toLowerCase().includes('error') ? 'text-danger' : ''}>{message.message}</div> : null}
                                      {message.status ? <div className='text-primary fw-semibold'>{message.status}</div> : null}
                                      {message.raw ? <div className='text-muted fst-italic'>{message.raw}</div> : null}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </Collapse>
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}

                  {/* Historical Executions (Latest First) */}
                  {historyTasks.map((item) => {
                    const rowKey = `history-${item.task_run_id}`;
                    const isExpanded = expandedRowId === rowKey;
                    const details = historyDetails[item.task_run_id];
                    const rawLogs = details?.raw_logs || [];
                    const finalLogs = details?.final_logs || [];

                    return (
                      <Fragment key={rowKey}>
                        <tr onClick={() => handleHistoryRowClick(item)} style={{ cursor: 'pointer' }}>
                          <td>
                            <div className='d-flex align-items-center gap-2 fw-semibold'>
                              <ChevronDown size={16} style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
                              {item.effectiveTitle}
                            </div>
                          </td>
                          <td>{item.username || '—'}</td>
                          <td>{item.task_name || '—'}</td>
                          <td>{item.task_type || 'Automation'}</td>
                          <td>{formatDate(item.task_start_time)}</td>
                          <td>{formatDate(item.task_completion_time)}</td>
                          <td>{item.agent_run_time || '—'}</td>
                          <td>
                            {renderTaskStatusBadge(item.task_status)}
                          </td>
                          <td className='text-end'>—</td>
                        </tr>
                        <tr>
                          <td colSpan={9} className='p-0 border-0'>
                            <Collapse isOpen={isExpanded}>
                              <div className='m-3 border rounded bg-light p-3'>
                                <div className='d-flex justify-content-between align-items-center mb-3'>
                                  <h6 className='mb-0 fw-bold'>Execution Logs ({item.effectiveTitle})</h6>
                                  <span className='text-muted small'>Task Run ID: {item.task_run_id}</span>
                                </div>

                                {details?.loading ? (
                                  <div className='text-center py-3'><Spinner size='sm' /> Loading execution details...</div>
                                ) : (
                                  <div style={{ maxHeight: 350, overflowY: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
                                    {rawLogs.length === 0 && finalLogs.length === 0 ? (
                                      <p className='text-muted mb-0'>No detailed logs stored for this execution.</p>
                                    ) : (
                                      <>
                                        {rawLogs.length > 0 && (
                                          <div className='mb-3'>
                                            <div className='fw-bold text-secondary mb-2'>Automation Raw Logs ({rawLogs.length})</div>
                                            {rawLogs.map((log: any, idx: number) => (
                                              <div key={`raw-${idx}`} className='border-bottom pb-1 mb-1'>
                                                {typeof log === 'string' ? (
                                                  <div>{log}</div>
                                                ) : (
                                                  <>
                                                    {log.timestamp ? <span className='text-muted me-2'>[{formatDate(log.timestamp)}]</span> : null}
                                                    {log.level ? <span className={`fw-semibold me-2 ${log.level === 'ERROR' ? 'text-danger' : 'text-info'}`}>{log.level}</span> : null}
                                                    <span>{log.message || log.text || JSON.stringify(log)}</span>
                                                  </>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {finalLogs.length > 0 && (
                                          <div>
                                            <div className='fw-bold text-primary mb-2'>Final Output Logs ({finalLogs.length})</div>
                                            {finalLogs.map((log: any, idx: number) => (
                                              <div key={`final-${idx}`} className='border-bottom pb-1 mb-1 text-primary'>
                                                {typeof log === 'string' ? log : log.message || log.text || JSON.stringify(log)}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            </Collapse>
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </>
              )}
            </tbody>
          </Table>
        </div>

        <div className='d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2'>
          <div className='d-flex align-items-center gap-2'>
            <span>Show</span>
            <Input type='select' value={perPage} onChange={(event) => { setPerPage(Number(event.target.value)); setPage(1); }} style={{ width: 90 }}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Input>
            <span>entries</span>
          </div>
          <div>Showing {Math.min((page - 1) * perPage + 1, totalHistory)} to {Math.min(page * perPage, totalHistory)} of {totalHistory} results</div>
          <div className='d-flex gap-2'>
            <Button color='light' disabled={page <= 1} onClick={() => setPage((previous) => Math.max(previous - 1, 1))}>Previous</Button>
            <span className='align-self-center'>{page} / {totalPages}</span>
            <Button color='light' disabled={page >= totalPages} onClick={() => setPage((previous) => Math.min(previous + 1, totalPages))}>Next</Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default BotAssistant;
